import React from 'react';
import { Activity, AlertTriangle, ArrowLeft, BarChart3, Bell, Brain, Briefcase, Building2, Calendar, CheckCircle, Eye, FileText, GraduationCap, Heart, Loader2, Maximize2, School, Send, Settings, Target, TrendingUp, Trophy, Users } from 'lucide-react';
import { StatCard } from '../SharedComponents';

const OverviewTab = ({ ctx }) => {
  const { org, summary, dashboard, usage, members, leaderboard, inHoliday, analytics, sentimentData, deptLeaderboard, insightAlerts, forecastData, alerts, schoolSettings, caseStudy, nudgeResult, maxUsage, API_URL, getToken, navigate, nudging, generatingCaseStudy, setNudging, setNudgeResult, setGeneratingCaseStudy, setCaseStudy, setShowBoardPresentation, benchmarks } = ctx;

  return (
<div data-testid="overview-tab">
  <div className="flex items-center justify-between mb-1">
    <h1 className="text-xl font-bold" style={{ color: '#1A1209' }}>
      {org?.org_name || 'Organisation'} Dashboard
    </h1>
    <div className="flex items-center gap-2">
      <button onClick={() => setShowBoardPresentation(true)}
        className="px-4 py-2 rounded-xl flex items-center gap-2 transition-all hover:scale-[1.02]"
        style={{ background: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)', boxShadow: '0 4px 14px rgba(99,102,241,0.3)' }}
        data-testid="board-presentation-btn">
        <Maximize2 size={14} style={{ color: 'white' }} />
        <span className="text-xs font-semibold text-white">Board Presentation</span>
      </button>
      <button onClick={() => navigate('/corporate/member-preview')}
        className="px-4 py-2 rounded-xl flex items-center gap-2 group transition-all hover:scale-[1.02]"
        style={{ background: 'linear-gradient(135deg, #1A1209 0%, #3D2E1A 100%)', border: '1px solid #F5831F33' }}
        data-testid="preview-student-btn">
        <Eye size={14} style={{ color: '#F5831F' }} />
        <span className="text-xs font-semibold" style={{ color: '#E8DDD0' }}>Preview Team Member Experience</span>
        <ArrowLeft size={11} className="rotate-180" style={{ color: '#F5831F' }} />
      </button>
    </div>
  </div>
  <p className="text-xs mb-5" style={{ color: '#999' }}>
    {org?.status === 'active' && org?.trial_end && `Free trial — expires ${new Date(org.trial_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`}
  </p>

  {/* Privacy banner */}
  <div className="flex items-center gap-2 px-4 py-2.5 mb-5 rounded-lg text-xs" style={{ backgroundColor: '#EFF6FF', color: '#2563EB', border: '1px solid #BFDBFE' }} data-testid="privacy-banner">
    <AlertTriangle size={14} />
    <span>This dashboard shows <strong>aggregated metrics only</strong>. Individual team member conversations are never accessible.</span>
  </div>

  {/* ── INSIGHT ALERTS ──────────────────────────────────── */}
  {insightAlerts?.alerts?.length > 0 && (
    <div className="mb-5 space-y-2" data-testid="insight-alerts">
      {insightAlerts.alerts.filter(a => !a.is_read).slice(0, 3).map(alert => {
        const colors = { warning: { bg: '#FEF2F2', border: '#FECACA', text: '#991B1B', icon: '#DC2626' }, success: { bg: '#F0FDF4', border: '#BBF7D0', text: '#166534', icon: '#16A34A' }, info: { bg: '#EFF6FF', border: '#BFDBFE', text: '#1E40AF', icon: '#3B82F6' } };
        const c = colors[alert.severity] || colors.info;
        return (
          <div key={alert.alert_id} className="flex items-start gap-3 px-4 py-3 rounded-xl transition-all" style={{ backgroundColor: c.bg, border: `1px solid ${c.border}` }} data-testid={`alert-${alert.alert_id}`}>
            <div className="mt-0.5 flex-shrink-0">
              {alert.severity === 'warning' ? <AlertTriangle size={14} style={{ color: c.icon }} /> : alert.severity === 'success' ? <CheckCircle size={14} style={{ color: c.icon }} /> : <Bell size={14} style={{ color: c.icon }} />}
            </div>
            <div className="flex-1">
              <p className="text-xs font-bold" style={{ color: c.text }}>{alert.title}</p>
              <p className="text-[10px] mt-0.5" style={{ color: c.text, opacity: 0.8 }}>{alert.body}</p>
            </div>
            <span className="text-[9px] flex-shrink-0 mt-0.5" style={{ color: c.text, opacity: 0.5 }}>
              {new Date(alert.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          </div>
        );
      })}
    </div>
  )}

  {/* Stats */}
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
    <StatCard label="Total Members" value={summary?.total || 0} icon={<Users size={18} style={{ color: '#F5831F' }} />} />
    <StatCard label="Teams" value={summary?.by_grade?.length || 0} icon={<GraduationCap size={18} style={{ color: '#2563EB' }} />} />
    <StatCard label="Team Leads" value={summary?.by_teacher?.length || 0} icon={<School size={18} style={{ color: '#7B4FD8' }} />} />
    <StatCard label="Sessions This Week" value={dashboard?.active_sessions_this_week || 0} icon={<BarChart3 size={18} style={{ color: '#2EAA5C' }} />} />
  </div>

  {/* Charts row */}
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
    {/* Grade Distribution */}
    <div className="bg-white rounded-xl p-4" style={{ border: '1px solid #E8E8E8' }}>
      <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>Members by Team</h3>
      {summary?.by_grade?.length > 0 ? (
        <div className="space-y-2">
          {summary.by_grade.map(g => (
            <div key={g.grade} className="flex items-center gap-2">
              <span className="text-xs w-20 text-right font-medium" style={{ color: '#666' }}>{g.grade}</span>
              <div className="flex-1 h-5 rounded-full overflow-hidden" style={{ backgroundColor: '#F0F0F0' }}>
                <div className="h-full rounded-full" style={{ width: `${(g.count / (summary.total || 1)) * 100}%`, backgroundColor: '#F5831F' }} />
              </div>
              <span className="text-xs font-bold w-8" style={{ color: '#333' }}>{g.count}</span>
            </div>
          ))}
        </div>
      ) : <p className="text-xs" style={{ color: '#AAA' }}>No members enrolled yet</p>}
    </div>

    {/* Weekly Usage */}
    <div className="bg-white rounded-xl p-4" style={{ border: '1px solid #E8E8E8' }}>
      <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>Weekly Usage (Sessions)</h3>
      {usage?.weekly_usage?.length > 0 ? (
        <div className="flex items-end gap-3 h-32">
          {usage.weekly_usage.map((w, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <span className="text-[10px] font-bold" style={{ color: '#333' }}>{w.sessions}</span>
              <div className="w-full rounded-t-md" style={{ height: `${(w.sessions / maxUsage) * 100}%`, minHeight: 4, backgroundColor: '#2563EB' }} />
              <span className="text-[10px]" style={{ color: '#999' }}>{w.week}</span>
            </div>
          ))}
        </div>
      ) : <p className="text-xs" style={{ color: '#AAA' }}>No usage data yet</p>}
    </div>
  </div>

  {/* Teacher summary */}
  {summary?.by_teacher?.length > 0 && (
    <div className="bg-white rounded-xl p-4" style={{ border: '1px solid #E8E8E8' }}>
      <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>Students by Teacher</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {summary.by_teacher.map(t => (
          <div key={t.teacher} className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ backgroundColor: '#F8F7F4' }}>
            <span className="text-xs font-medium" style={{ color: '#333' }}>{t.teacher}</span>
            <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F5831F15', color: '#F5831F' }}>{t.count}</span>
          </div>
        ))}
      </div>
    </div>
  )}

  {/* Leaderboard + Nudge */}
  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
    {/* Leaderboard */}
    {leaderboard.length > 0 && (
      <div className="bg-white rounded-xl p-4" style={{ border: '1px solid #E8E8E8' }} data-testid="leaderboard-card">
        <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: '#1A1209' }}>
          <Trophy size={14} style={{ color: '#F5831F' }} /> Top Achievers This Month
        </h3>
        <div className="space-y-1.5">
          {leaderboard.slice(0, 5).map((s, i) => (
            <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-lg" style={{ backgroundColor: i < 3 ? ['#FFF7ED', '#F0F9FF', '#FEF3C7'][i] : '#F8F7F4' }}>
              <span className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black" style={{
                backgroundColor: i < 3 ? ['#F5831F', '#3B82F6', '#F59E0B'][i] : '#DDD',
                color: i < 3 ? 'white' : '#999',
              }}>{i + 1}</span>
              <div className="flex-1">
                <span className="text-xs font-semibold" style={{ color: '#1A1209' }}>{s.first_name}</span>
                <span className="text-[10px] ml-2" style={{ color: '#999' }}>{s.grade}</span>
              </div>
              <div className="flex gap-3 text-[10px]" style={{ color: '#888' }}>
                <span>{s.challenges} challenges</span>
                <span>{s.sessions} sessions</span>
              </div>
              <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F5831F15', color: '#F5831F' }}>{s.score}</span>
            </div>
          ))}
        </div>
      </div>
    )}

    {/* Nudge Panel */}
    <div className="bg-white rounded-xl p-4" style={{ border: '1px solid #E8E8E8' }} data-testid="nudge-card">
      <h3 className="text-sm font-bold mb-2 flex items-center gap-2" style={{ color: '#1A1209' }}>
        <Bell size={14} style={{ color: '#2563EB' }} /> Member Nudges
      </h3>
      <p className="text-xs mb-3" style={{ color: '#999' }}>
        Send friendly reminders to members who haven&apos;t been active in {schoolSettings.nudge_inactive_days} days.
        Adjust the threshold in Settings.
      </p>
      <button
        onClick={async () => {
          setNudging(true); setNudgeResult(null);
          try {
            const res = await fetch(`${API_URL}/api/corporate/nudge`, {
              method: 'POST', headers: { Authorization: `Bearer ${getToken()}` },
            });
            if (res.ok) setNudgeResult(await res.json());
          } catch {}
          setNudging(false);
        }}
        disabled={nudging}
        className="w-full py-2.5 rounded-lg text-sm font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50"
        style={{ backgroundColor: '#2563EB' }}
        data-testid="send-nudge-btn"
      >
        {nudging ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        {nudging ? 'Sending...' : 'Send Nudges to Inactive Members'}
      </button>
      {nudgeResult && (
        <div className="mt-3 p-3 rounded-lg" style={{ backgroundColor: nudgeResult.nudged_count > 0 ? '#ECFDF5' : '#F0F9FF' }}>
          <p className="text-xs font-semibold" style={{ color: nudgeResult.nudged_count > 0 ? '#065F46' : '#1E40AF' }}>
            {nudgeResult.nudged_count > 0 ? `Nudged ${nudgeResult.nudged_count} students` : 'All students are active — no nudges needed!'}
          </p>
          {nudgeResult.nudged_students?.slice(0, 5).map((s, i) => (
            <span key={i} className="text-[10px] mr-2" style={{ color: '#059669' }}>{s.name}</span>
          ))}
        </div>
      )}
    </div>
  </div>

  {/* Benchmarks */}
  {benchmarks && (
    <div className="bg-white rounded-xl p-5 mt-5" style={{ border: '1px solid #E8E8E8' }} data-testid="benchmarks-card">
      <h3 className="text-sm font-bold mb-4 flex items-center gap-2" style={{ color: '#1A1209' }}>
        <TrendingUp size={14} style={{ color: '#8B5CF6' }} /> How You Compare (Platform Benchmarks)
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[
          { label: 'Sessions / Student (30d)', yours: benchmarks.org?.sessions_per_student, platform: benchmarks.platform?.sessions_per_student, delta: benchmarks.deltas?.sessions },
          { label: 'Challenges / Student', yours: benchmarks.org?.challenges_per_student, platform: benchmarks.platform?.challenges_per_student, delta: benchmarks.deltas?.challenges },
        ].map(b => (
          <div key={b.label} className="p-4 rounded-xl" style={{ backgroundColor: '#F8F7F4' }}>
            <p className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: '#999' }}>{b.label}</p>
            <div className="flex items-end gap-4">
              <div>
                <p className="text-2xl font-bold" style={{ color: '#1A1209' }}>{b.yours}</p>
                <p className="text-[10px]" style={{ color: '#999' }}>Your organisation</p>
              </div>
              <div>
                <p className="text-lg font-semibold" style={{ color: '#999' }}>{b.platform}</p>
                <p className="text-[10px]" style={{ color: '#CCC' }}>Platform avg</p>
              </div>
              {b.delta !== 0 && (
                <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{
                  backgroundColor: b.delta > 0 ? '#ECFDF5' : b.delta < 0 ? '#FEF2F2' : '#F0F0F0',
                  color: b.delta > 0 ? '#059669' : b.delta < 0 ? '#DC2626' : '#999',
                }}>
                  {b.delta > 0 ? '+' : ''}{b.delta}%
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
      <p className="text-[10px] mt-3" style={{ color: '#CCC' }}>
        Compared across {benchmarks.platform?.total_schools} schools, {benchmarks.platform?.total_students} students
      </p>
    </div>
  )}

  {/* Academic Calendar Status */}
  {inHoliday && (
    <div className="mt-5 flex items-center gap-3 px-4 py-3 rounded-xl" style={{ backgroundColor: '#FFFBEB', border: '1px solid #FEF3C7' }} data-testid="holiday-banner">
      <Calendar size={16} style={{ color: '#F59E0B' }} />
      <p className="text-xs font-medium" style={{ color: '#92400E' }}>
        Organisation is currently on holiday. Nudges and inactivity alerts are paused.
      </p>
    </div>
  )}

  {/* ── ANALYTICS SECTION ─────────────────────────────────── */}
  {analytics && (
    <div className="space-y-5 mt-6" data-testid="analytics-section">
      {analytics.demo_data && (
        <div className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium" style={{ backgroundColor: '#EFF6FF', color: '#1E40AF', border: '1px solid #BFDBFE' }}>
          <BarChart3 size={14} />
          Sample data — showing what your dashboard will look like with active team members
        </div>
      )}

      {/* ── BIG FIVE / OCEAN PERSONALITY ASSESSMENT ──────────── */}
      <div className="bg-white rounded-xl p-5" style={{ border: '1px solid #E8E8E8' }} data-testid="ocean-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: '#1A1209' }}>
              <Brain size={16} style={{ color: '#8B5CF6' }} />
              Big Five Personality Assessment (OCEAN)
            </h3>
            <p className="text-[10px] mt-1" style={{ color: '#999' }}>
              Built into every team member&apos;s first login. Auto-calibrates AvA responses to individual personality profiles.
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-black" style={{ color: '#8B5CF6' }}>{analytics.ocean.completion_rate}%</p>
            <p className="text-[10px]" style={{ color: '#999' }}>{analytics.ocean.completed_count}/{analytics.ocean.total_count} completed</p>
          </div>
        </div>

        {/* OCEAN completion bar */}
        <div className="w-full h-2 rounded-full mb-5" style={{ backgroundColor: '#F0F0F0' }}>
          <div className="h-full rounded-full transition-all" style={{ width: `${analytics.ocean.completion_rate}%`, background: 'linear-gradient(90deg, #8B5CF6, #6D28D9)' }} />
        </div>

        {/* OCEAN Trait Bars */}
        <div className="space-y-3">
          {['openness', 'conscientiousness', 'extraversion', 'agreeableness', 'neuroticism'].map(trait => {
            const score = analytics.ocean.aggregate_scores[trait] || 0;
            const pct = (score / 5) * 100;
            const colors = {
              openness: '#3B82F6', conscientiousness: '#10B981', extraversion: '#F59E0B',
              agreeableness: '#EC4899', neuroticism: '#EF4444',
            };
            return (
              <div key={trait} data-testid={`ocean-trait-${trait}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold capitalize" style={{ color: '#1A1209' }}>{trait}</span>
                    <span className="text-[10px]" style={{ color: '#BBB' }}>{analytics.ocean.trait_descriptions[trait]}</span>
                  </div>
                  <span className="text-sm font-black" style={{ color: colors[trait] }}>{score}</span>
                </div>
                <div className="w-full h-2.5 rounded-full" style={{ backgroundColor: '#F0F0F0' }}>
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: colors[trait] }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Department OCEAN breakdown */}
        {analytics.ocean.by_department && Object.keys(analytics.ocean.by_department).length > 0 && (
          <div className="mt-5 pt-4" style={{ borderTop: '1px solid #F0F0F0' }}>
            <h4 className="text-xs font-bold mb-3 flex items-center gap-2" style={{ color: '#1A1209' }}>
              <Building2 size={12} style={{ color: '#6B7280' }} />
              Department Personality Profiles
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(analytics.ocean.by_department).slice(0, 6).map(([dept, scores]) => (
                <div key={dept} className="p-3 rounded-lg" style={{ backgroundColor: '#FAFAF8' }}>
                  <p className="text-[11px] font-bold mb-2" style={{ color: '#1A1209' }}>{dept}</p>
                  <div className="space-y-1.5">
                    {['openness', 'conscientiousness', 'extraversion', 'agreeableness', 'neuroticism'].map(t => {
                      const v = scores[t] || 0;
                      const colors = { openness: '#3B82F6', conscientiousness: '#10B981', extraversion: '#F59E0B', agreeableness: '#EC4899', neuroticism: '#EF4444' };
                      return (
                        <div key={t} className="flex items-center gap-2">
                          <span className="text-[9px] font-semibold w-[14px] uppercase" style={{ color: '#999' }}>{t[0]}</span>
                          <div className="flex-1 h-1.5 rounded-full" style={{ backgroundColor: '#E8E8E8' }}>
                            <div className="h-full rounded-full" style={{ width: `${(v / 5) * 100}%`, backgroundColor: colors[t] }} />
                          </div>
                          <span className="text-[9px] font-bold w-[20px] text-right" style={{ color: colors[t] }}>{v}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── ROI DASHBOARD ──────────────────────────────────── */}
      <div className="bg-white rounded-xl p-5" style={{ border: '1px solid #E8E8E8' }} data-testid="roi-card">
        <h3 className="text-sm font-bold mb-4 flex items-center gap-2" style={{ color: '#1A1209' }}>
          <TrendingUp size={16} style={{ color: '#059669' }} />
          ROI & Impact Metrics
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {[
            { label: 'Confidence Lift', value: `+${analytics.roi.confidence_lift_pct}%`, sub: 'vs. baseline', color: '#059669', bg: '#ECFDF5' },
            { label: 'Communication', value: `+${analytics.roi.communication_improvement_pct}%`, sub: 'improvement', color: '#3B82F6', bg: '#EFF6FF' },
            { label: 'Conflict Resolution', value: `+${analytics.roi.conflict_resolution_improvement_pct}%`, sub: 'improvement', color: '#8B5CF6', bg: '#F5F3FF' },
            { label: 'Engagement Rate', value: `${analytics.roi.engagement_rate_pct}%`, sub: 'active members', color: '#F59E0B', bg: '#FFFBEB' },
            { label: 'Avg Sessions / Member', value: analytics.roi.sessions_per_member_30d, sub: 'last 30 days', color: '#1A1209', bg: '#F8F7F4' },
            { label: 'Est. Annual Savings', value: `$${analytics.roi.estimated_annual_savings_per_employee}`, sub: 'per employee', color: '#059669', bg: '#ECFDF5' },
            { label: 'Retention Impact', value: `+${analytics.roi.retention_impact_pct}%`, sub: 'estimated lift', color: '#EC4899', bg: '#FDF2F8' },
            { label: 'Team Members', value: analytics.total_members, sub: 'enrolled', color: '#6B7280', bg: '#F3F4F6' },
          ].map(m => (
            <div key={m.label} className="p-3 rounded-xl text-center" style={{ backgroundColor: m.bg }} data-testid={`roi-${m.label.toLowerCase().replace(/\s/g, '-')}`}>
              <p className="text-xl font-black" style={{ color: m.color }}>{m.value}</p>
              <p className="text-[10px] font-bold mt-0.5" style={{ color: '#1A1209' }}>{m.label}</p>
              <p className="text-[9px]" style={{ color: '#999' }}>{m.sub}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── DEPARTMENT ANALYTICS ───────────────────────────── */}
      {analytics.departments?.length > 0 && (
        <div className="bg-white rounded-xl p-5" style={{ border: '1px solid #E8E8E8' }} data-testid="dept-analytics-card">
          <h3 className="text-sm font-bold mb-4 flex items-center gap-2" style={{ color: '#1A1209' }}>
            <Building2 size={16} style={{ color: '#F5831F' }} />
            Department Performance
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider" style={{ color: '#999' }}>
                  <th className="text-left pb-3 font-semibold">Department</th>
                  <th className="text-center pb-3 font-semibold">Members</th>
                  <th className="text-center pb-3 font-semibold">OCEAN Done</th>
                  <th className="text-center pb-3 font-semibold">Active Rate</th>
                  <th className="text-center pb-3 font-semibold">Avg Sessions</th>
                  <th className="text-center pb-3 font-semibold">Confidence</th>
                  <th className="text-left pb-3 font-semibold">Top Skill</th>
                </tr>
              </thead>
              <tbody>
                {analytics.departments.map((d, i) => (
                  <tr key={d.department} className="border-t" style={{ borderColor: '#F0F0F0' }}>
                    <td className="py-2.5 font-bold" style={{ color: '#1A1209' }}>
                      <div className="flex items-center gap-2">
                        {i === 0 && <span className="text-[9px] px-1.5 py-0.5 rounded font-black text-white" style={{ backgroundColor: '#F5831F' }}>TOP</span>}
                        {d.department}
                      </div>
                    </td>
                    <td className="text-center py-2.5" style={{ color: '#666' }}>{d.members}</td>
                    <td className="text-center py-2.5">
                      <span className="font-bold" style={{ color: d.ocean_completed >= d.members * 0.7 ? '#059669' : '#F59E0B' }}>
                        {d.ocean_completed}/{d.members}
                      </span>
                    </td>
                    <td className="text-center py-2.5">
                      <span className="font-bold px-2 py-0.5 rounded-full text-[10px]" style={{
                        backgroundColor: d.active_rate >= 70 ? '#ECFDF5' : d.active_rate >= 50 ? '#FFFBEB' : '#FEF2F2',
                        color: d.active_rate >= 70 ? '#059669' : d.active_rate >= 50 ? '#D97706' : '#DC2626',
                      }}>{d.active_rate}%</span>
                    </td>
                    <td className="text-center py-2.5 font-semibold" style={{ color: '#1A1209' }}>{d.avg_sessions}</td>
                    <td className="text-center py-2.5">
                      <span className="font-bold" style={{ color: '#059669' }}>+{d.confidence_delta}%</span>
                    </td>
                    <td className="py-2.5 text-[10px]" style={{ color: '#888' }}>{d.top_skill}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── INDUSTRY BENCHMARKS ────────────────────────────── */}
      {analytics.benchmarks && (
        <div className="bg-white rounded-xl p-5" style={{ border: '1px solid #E8E8E8' }} data-testid="industry-benchmarks-card">
          <h3 className="text-sm font-bold mb-4 flex items-center gap-2" style={{ color: '#1A1209' }}>
            <BarChart3 size={16} style={{ color: '#6366F1' }} />
            Industry Benchmarks — {analytics.benchmarks.industry}
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Sessions / Member', yours: analytics.benchmarks.your_sessions_per_member, avg: analytics.benchmarks.industry_avg_sessions, delta: analytics.benchmarks.sessions_delta_pct },
              { label: 'OCEAN Completion', yours: `${analytics.benchmarks.your_ocean_completion}%`, avg: `${analytics.benchmarks.industry_avg_ocean_completion}%`, delta: analytics.benchmarks.your_ocean_completion - analytics.benchmarks.industry_avg_ocean_completion },
              { label: 'Engagement Rate', yours: `${analytics.benchmarks.your_engagement_rate}%`, avg: `${analytics.benchmarks.industry_avg_engagement}%`, delta: analytics.benchmarks.your_engagement_rate - analytics.benchmarks.industry_avg_engagement },
              { label: 'Confidence Lift', yours: `${analytics.benchmarks.your_confidence_lift}%`, avg: `${analytics.benchmarks.industry_avg_confidence_lift}%`, delta: analytics.benchmarks.your_confidence_lift - analytics.benchmarks.industry_avg_confidence_lift },
            ].map(b => (
              <div key={b.label} className="p-4 rounded-xl" style={{ backgroundColor: '#F8F7F4' }}>
                <p className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: '#999' }}>{b.label}</p>
                <p className="text-2xl font-black" style={{ color: '#1A1209' }}>{b.yours}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[10px]" style={{ color: '#BBB' }}>Industry: {b.avg}</span>
                  {b.delta !== 0 && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{
                      backgroundColor: b.delta > 0 ? '#ECFDF5' : '#FEF2F2',
                      color: b.delta > 0 ? '#059669' : '#DC2626',
                    }}>
                      {b.delta > 0 ? '+' : ''}{typeof b.delta === 'number' ? Math.round(b.delta) : b.delta}%
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── WEEKLY ENGAGEMENT TREND ────────────────────────── */}
      {analytics.weekly_trend?.length > 0 && (
        <div className="bg-white rounded-xl p-5" style={{ border: '1px solid #E8E8E8' }} data-testid="weekly-trend-card">
          <h3 className="text-sm font-bold mb-4 flex items-center gap-2" style={{ color: '#1A1209' }}>
            <Activity size={16} style={{ color: '#3B82F6' }} />
            8-Week Engagement Trend
          </h3>
          <div className="flex items-end gap-2 h-32">
            {analytics.weekly_trend.map((w, i) => {
              const maxSessions = Math.max(...analytics.weekly_trend.map(x => x.sessions), 1);
              const height = Math.max(8, (w.sessions / maxSessions) * 100);
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-[9px] font-bold" style={{ color: '#1A1209' }}>{w.sessions}</span>
                  <div className="w-full rounded-t-md transition-all" style={{
                    height: `${height}%`,
                    background: i >= analytics.weekly_trend.length - 2 ? 'linear-gradient(180deg, #3B82F6, #1D4ED8)' : '#E0E7FF',
                  }} />
                  <span className="text-[9px]" style={{ color: '#999' }}>{w.week}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── SKILLS BREAKDOWN ───────────────────────────────── */}
      {analytics.skills_breakdown?.length > 0 && (
        <div className="bg-white rounded-xl p-5" style={{ border: '1px solid #E8E8E8' }} data-testid="skills-card">
          <h3 className="text-sm font-bold mb-4 flex items-center gap-2" style={{ color: '#1A1209' }}>
            <Target size={16} style={{ color: '#F5831F' }} />
            Skills Practiced
          </h3>
          <div className="space-y-2.5">
            {analytics.skills_breakdown.map((s, i) => {
              const maxCount = analytics.skills_breakdown[0]?.sessions || 1;
              const pct = (s.sessions / maxCount) * 100;
              const colors = ['#F5831F', '#3B82F6', '#10B981', '#8B5CF6', '#EC4899', '#F59E0B', '#EF4444', '#6366F1'];
              return (
                <div key={s.skill}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold" style={{ color: '#1A1209' }}>{s.skill}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px]" style={{ color: '#999' }}>{s.sessions} sessions</span>
                      {s.improvement_pct > 0 && (
                        <span className="text-[10px] font-bold" style={{ color: '#059669' }}>+{s.improvement_pct}%</span>
                      )}
                    </div>
                  </div>
                  <div className="w-full h-2 rounded-full" style={{ backgroundColor: '#F0F0F0' }}>
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: colors[i % colors.length] }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── SENTIMENT TREND ────────────────────────────────── */}
      {sentimentData?.trend?.length > 0 && (
        <div className="bg-white rounded-xl p-5" style={{ border: '1px solid #E8E8E8' }} data-testid="sentiment-trend-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: '#1A1209' }}>
              <Heart size={16} style={{ color: '#EC4899' }} />
              12-Week Sentiment Trend
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-black" style={{ color: sentimentData.current_score >= 3.8 ? '#059669' : sentimentData.current_score >= 3.0 ? '#F59E0B' : '#DC2626' }}>
                {sentimentData.current_score}
              </span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{
                backgroundColor: sentimentData.trend_direction === 'up' ? '#ECFDF5' : '#FEF2F2',
                color: sentimentData.trend_direction === 'up' ? '#059669' : '#DC2626',
              }}>
                {sentimentData.trend_direction === 'up' ? 'Trending Up' : 'Trending Down'}
              </span>
            </div>
          </div>
          {sentimentData.demo_data && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-medium mb-3" style={{ backgroundColor: '#EFF6FF', color: '#1E40AF' }}>
              <BarChart3 size={10} /> Sample data
            </div>
          )}
          <div className="flex items-end gap-1.5 h-28">
            {sentimentData.trend.map((w, i) => {
              const maxScore = 5;
              const height = Math.max(8, (w.avg_sentiment / maxScore) * 100);
              const color = w.label === 'positive' ? '#10B981' : w.label === 'neutral' ? '#F59E0B' : '#EF4444';
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1" data-testid={`sentiment-week-${i}`}>
                  <span className="text-[8px] font-bold" style={{ color }}>{w.avg_sentiment}</span>
                  <div className="w-full rounded-t-md transition-all" style={{ height: `${height}%`, backgroundColor: color, opacity: i === sentimentData.trend.length - 1 ? 1 : 0.6 }} />
                  <span className="text-[8px]" style={{ color: '#BBB' }}>{w.date}</span>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-4 mt-3 pt-3" style={{ borderTop: '1px solid #F0F0F0' }}>
            {[{ label: 'Positive', color: '#10B981' }, { label: 'Neutral', color: '#F59E0B' }, { label: 'Needs Attention', color: '#EF4444' }].map(l => (
              <div key={l.label} className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: l.color }} />
                <span className="text-[9px]" style={{ color: '#999' }}>{l.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── ENGAGEMENT FORECAST ────────────────────────────── */}
      {forecastData && (
        <div className="bg-white rounded-xl p-5" style={{ border: '1px solid #E8E8E8' }} data-testid="forecast-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: '#1A1209' }}>
              <TrendingUp size={16} style={{ color: '#6366F1' }} />
              12-Week Engagement Forecast
            </h3>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{
              backgroundColor: forecastData.trajectory === 'improving' ? '#ECFDF5' : forecastData.trajectory === 'stable' ? '#EFF6FF' : '#FEF2F2',
              color: forecastData.trajectory === 'improving' ? '#059669' : forecastData.trajectory === 'stable' ? '#2563EB' : '#DC2626',
            }}>
              {forecastData.trajectory === 'improving' ? 'Trending Up' : forecastData.trajectory === 'stable' ? 'Stable' : 'Declining'}
              {forecastData.change_predicted > 0 ? ` (+${forecastData.change_predicted}%)` : ` (${forecastData.change_predicted}%)`}
            </span>
          </div>
          {forecastData.demo_data && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-medium mb-3" style={{ backgroundColor: '#EFF6FF', color: '#1E40AF' }}>
              <BarChart3 size={10} /> Predictive model based on sample data
            </div>
          )}
          <div className="flex items-end gap-1 h-32">
            {[...forecastData.historical, ...forecastData.forecast].map((w, i) => {
              const maxVal = 100;
              const height = Math.max(8, (w.engagement / maxVal) * 100);
              const isForecast = w.type === 'forecast';
              const color = isForecast ? '#6366F1' : '#1A1209';
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1" data-testid={`forecast-bar-${i}`}>
                  <span className="text-[7px] font-bold" style={{ color }}>{w.engagement}%</span>
                  <div className="w-full rounded-t-sm transition-all" style={{
                    height: `${height}%`,
                    backgroundColor: color,
                    opacity: isForecast ? 0.4 + (w.confidence || 0.5) * 0.5 : 0.8,
                    borderTop: isForecast ? '2px dashed #6366F1' : 'none',
                  }} />
                  <span className="text-[7px]" style={{ color: '#CCC' }}>{w.week}</span>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-4 mt-3 pt-3" style={{ borderTop: '1px solid #F0F0F0' }}>
            <div className="flex items-center gap-1.5"><div className="w-3 h-2 rounded-sm" style={{ backgroundColor: '#1A1209', opacity: 0.8 }} /><span className="text-[9px]" style={{ color: '#999' }}>Actual</span></div>
            <div className="flex items-center gap-1.5"><div className="w-3 h-2 rounded-sm" style={{ backgroundColor: '#6366F1', opacity: 0.6 }} /><span className="text-[9px]" style={{ color: '#999' }}>Forecast</span></div>
          </div>
        </div>
      )}

      {/* ── DEPARTMENT LEADERBOARD ──────────────────────────── */}
      {deptLeaderboard?.leaderboard?.length > 0 && (
        <div className="bg-white rounded-xl p-5" style={{ border: '1px solid #E8E8E8' }} data-testid="dept-leaderboard-card">
          <h3 className="text-sm font-bold mb-4 flex items-center gap-2" style={{ color: '#1A1209' }}>
            <Trophy size={16} style={{ color: '#F59E0B' }} />
            Department Competition Leaderboard
          </h3>
          {deptLeaderboard.demo_data && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-medium mb-3" style={{ backgroundColor: '#EFF6FF', color: '#1E40AF' }}>
              <BarChart3 size={10} /> Sample data
            </div>
          )}
          <div className="space-y-2">
            {deptLeaderboard.leaderboard.map((d, i) => {
              const medal = i < 3 ? ['#F5831F', '#94A3B8', '#CD7F32'][i] : null;
              return (
                <div key={d.department} className="flex items-center gap-3 px-4 py-3 rounded-lg" style={{ backgroundColor: i === 0 ? '#FFF7ED' : i < 3 ? '#F8FAFC' : '#FAFAF8' }} data-testid={`dept-lb-${i}`}>
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-black" style={{ backgroundColor: medal || '#E5E7EB', color: medal ? 'white' : '#9CA3AF' }}>
                    {d.rank}
                  </div>
                  <div className="flex-1">
                    <span className="text-xs font-bold" style={{ color: '#1A1209' }}>{d.department}</span>
                    <span className="text-[10px] ml-2" style={{ color: '#999' }}>{d.members} members</span>
                  </div>
                  <div className="flex gap-4 text-[10px]" style={{ color: '#666' }}>
                    <span><strong style={{ color: '#1A1209' }}>{d.avg_per_member_week}</strong> avg/member</span>
                    <span>{d.sessions_this_week} sessions</span>
                    <span>{d.challenges_completed} challenges</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── CASE STUDY GENERATOR ────────────────────────────── */}
      <div className="bg-white rounded-xl p-5" style={{ border: '1px solid #E8E8E8' }} data-testid="case-study-card">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: '#1A1209' }}>
              <Briefcase size={16} style={{ color: '#6366F1' }} />
              Impact Report Generator
            </h3>
            <p className="text-[10px] mt-1" style={{ color: '#999' }}>
              Auto-generate a branded case study highlighting your organisation&apos;s HiBFF ROI and outcomes.
            </p>
          </div>
          <button
            onClick={async () => {
              setGeneratingCaseStudy(true);
              try {
                const res = await fetch(`${API_URL}/api/corporate/case-study`, { headers: { Authorization: `Bearer ${getToken()}` } });
                if (res.ok) {
                  const data = await res.json();
                  setCaseStudy(data);
                }
              } catch {}
              setGeneratingCaseStudy(false);
            }}
            disabled={generatingCaseStudy}
            className="px-4 py-2 rounded-lg text-xs font-semibold text-white flex items-center gap-1.5 disabled:opacity-50"
            style={{ backgroundColor: '#6366F1' }}
            data-testid="generate-case-study-btn"
          >
            {generatingCaseStudy ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
            {generatingCaseStudy ? 'Generating...' : caseStudy ? 'Regenerate' : 'Generate Report'}
          </button>
        </div>
        {caseStudy && (
          <div className="mt-4 space-y-4" data-testid="case-study-content">
            <div className="p-4 rounded-xl" style={{ background: 'linear-gradient(135deg, #1A1209, #3D2E1A)' }}>
              <p className="text-[10px] uppercase tracking-widest" style={{ color: '#F5831F' }}>Impact Report</p>
              <h2 className="text-lg font-bold text-white mt-1">{caseStudy.title}</h2>
              <p className="text-xs mt-1" style={{ color: '#A89880' }}>{caseStudy.subtitle}</p>
              <div className="flex gap-3 mt-3">
                <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F5831F22', color: '#F5831F' }}>{caseStudy.industry}</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F5831F22', color: '#F5831F' }}>{caseStudy.team_size} team members</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F5831F22', color: '#F5831F' }}>{caseStudy.duration}</span>
              </div>
            </div>
            {caseStudy.sections?.map((section, si) => (
              <div key={si} className="p-4 rounded-lg" style={{ backgroundColor: '#F8F7F4' }}>
                <h4 className="text-xs font-bold mb-2" style={{ color: '#1A1209' }}>{section.heading}</h4>
                {section.content && <p className="text-xs leading-relaxed" style={{ color: '#666', whiteSpace: 'pre-line' }}>{section.content}</p>}
                {section.metrics && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                    {section.metrics.map(m => (
                      <div key={m.label} className="bg-white p-3 rounded-lg text-center">
                        <p className="text-lg font-black" style={{ color: '#F5831F' }}>{m.value}</p>
                        <p className="text-[10px] font-bold" style={{ color: '#1A1209' }}>{m.label}</p>
                        <p className="text-[9px]" style={{ color: '#999' }}>{m.description}</p>
                      </div>
                    ))}
                  </div>
                )}
                {section.bullets && (
                  <ul className="space-y-1.5 mt-1">
                    {section.bullets.map((b, bi) => (
                      <li key={bi} className="flex items-start gap-2 text-xs" style={{ color: '#666' }}>
                        <CheckCircle size={12} className="mt-0.5 flex-shrink-0" style={{ color: '#10B981' }} />
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )}
</div>
  );
};

export default OverviewTab;
